<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" :id="offcanvas_id" data-bs-backdrop="static" :aria-labelledby="`${offcanvas_id}_label`">
    <div class="offcanvas-header">
      <button class="btn-back" type="button" name="button" data-test="btnCloseOffcanvas" data-bs-dismiss="offcanvas" aria-label="Close" @click="doCancel">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block" :id="`${offcanvas_id}_label`">{{ title }}</h5>
    </div>
    <div class="offcanvas-body">
      <vee-form v-slot="{ handleSubmit }" as="div">
        <form @submit.stop.prevent="handleSubmit(doUpdateClinicOwner)">
          <p>{{ text }}</p>
          <div class="form-floating">
            <vee-field name="email" v-model="newEmail" rules="required|email" v-slot="{ field, errorMessage }">
              <input v-bind="field" class="form-control" type="email" id="email" :placeholder="$t('clinic.emailPlaceholder')" ref="email" autocomplete="off" data-test="inputPermissionEmail">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="email">{{ $t('clinic.emailPlaceholder') }}</label>
          </div>

          <!-- footer inside body to avoid empty space between body and footer -->
          <div class="offcanvas-footer">
            <button type="submit" class="btn" :class="okButtonClass" data-test="btnConfirm">{{ okButton }}</button>
          </div>
        </form>
      </vee-form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.vee-error {
  width:                                100% !important;
}
.offcanvas {
  .offcanvas-body {
    p {
      color:                            var(--Brand-Primary, #1B294A);
      font-size:                        var(--font-size-14);
      font-weight:                      300;
      line-height:                      140%; /* 19.6px */
      margin-bottom:                    0;
    }

    form {
      display:                          flex;
      flex-direction:                   column;
      gap:                              26px;
    }
  }

  .offcanvas-footer {
    display:                            flex;
    flex-direction:                     column;
    gap:                                26px;

    .btn {
      font-size:                        var(--font-size-14);
      font-weight:                      700;
      line-height:                      normal;
      letter-spacing:                   4.2px;
      text-align:                       center;
      text-transform:                   uppercase;
      width:                            100%;
      padding:                          20px;
    }

    .btn-success {
      color:                            var(--Shades-W, #FFF);
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'

export default {
  name: 'ClinicUpdateOwnerOffcanvas',
  props: ['offcanvas_id'],
  data: () => ({
    title: null,
    text: null,
    okButton: null,
    okButtonClass: null,
    offcanvas_change_owner: null,
    newEmail: null,
    oldEmail: null,
    clinicID: null,
    userID: null,
    session: {}
  }),
  mounted() {
  },
  methods: {
    show(options = {}) {
      this.newEmail = null
      this.oldEmail = null
      this.clinicID = null
      this.userID = null

      if (options.title)
        this.title = options.title

      if (options.text)
        this.text = options.text

      if (options.okButton) {
        this.okButton = options.okButton
        this.okButtonClass = options.okButtonClass
      }

      if (options.clinicID)
        this.clinicID = options.clinicID

      const userStore = useUserStore()
      this.session = userStore.sessionInfo

      this.fetchData()

      const element = document.getElementById(this.offcanvas_id);
      this.offcanvas_change_owner = new bootstrap.Offcanvas(element);
      this.offcanvas_change_owner.show();

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },

    doConfirm() {
      this.doClose()
      this.resolvePromise(true)
    },

    doCancel() {
      this.doClose()
      this.resolvePromise(false)
    },

    doClose() {
      if (this.offcanvas_change_owner) {
        this.offcanvas_change_owner.hide();
        this.offcanvas_change_owner = null;
      }
    },

    async fetchData() {
      NProgress.start()
      try {
        const response = await this.$clinicService.getClinicOwner({clinicId: this.clinicID});

        this.userID = response.id
        this.oldEmail = response.email
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      } finally {
        NProgress.done(true)
      }
    },

    async doUpdateClinicOwner() {
      if (this.newEmail === this.oldEmail) {
        this.$message.popup(this, 'clinic.changeOwnerSameEmail', 'operationFail', 'error')
        return
      }

      const params = {
        id: this.clinicID,
        oldEmail: this.oldEmail,
        newEmail: this.newEmail
      }

      NProgress.start()
      try {
        await this.$clinicService.updateClinicOwner(params);
        this.$message.popup(this, 'general.updatedSuccess', 'operationSuccess', 'success')

        this.$nextTick(() => {
          this.updateOldEmailPermission()
        })
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage
        if (errorMessage === 'userNotFound')
          this.$message.popup(this, 'clinic.changeOwnerUserNotFound', 'operationFail', 'error')
        else if (errorMessage === 'userNotInClinic')
          this.$message.popup(this, 'clinic.changeOwnerUserNotInClinic', 'operationFail', 'error')
        else
          this.$message.popup(this, 'general.updatedError', 'operationFail', 'error')
      } finally {
        NProgress.done(true)
      }
    },

    async updateOldEmailPermission() {
      NProgress.start()
      try {
        await this.$userService.changePermission(this.userID, 'admin');
        this.$message.popup(this, 'general.updatedSuccess', 'operationSuccess', 'success')

        if (this.oldEmail == this.session.collaborator.email)
          await this.$authService.refreshSession(this)

        this.$nextTick(() => {
          this.doConfirm()
        })
      } catch (err) {
        this.$message.popup(this, 'general.updatedError', 'operationFail', 'error')
      } finally {
        NProgress.done(true)
      }
    }
  }
}
</script>
